// Firestore Service - CRUD operations for user data
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    onSnapshot,
    Unsubscribe,
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../config/firebase';
import { Task, Project, CalendarEvent, InboxItem, Habit, Goal, AIPersonaId } from '../contexts/AppContext';

// User data structure
export interface UserData {
    tasks: Task[];
    projects: Project[];
    events: CalendarEvent[];
    inboxItems: InboxItem[];
    habits: Habit[];
    goals: Goal[];
    settings: {
        timezone: string;
        aiPersona: AIPersonaId;
    };
    lastUpdated: string;
}

// Default user data
const defaultUserData: UserData = {
    tasks: [],
    projects: [],
    events: [],
    inboxItems: [],
    habits: [],
    goals: [],
    settings: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        aiPersona: 'friend',
    },
    lastUpdated: new Date().toISOString(),
};

// Get user document reference
const getUserDocRef = (userId: string) => {
    if (!db) throw new Error('Firestore not initialized');
    return doc(db, 'users', userId);
};

// Fetch user data
export const fetchUserData = async (userId: string): Promise<UserData | null> => {
    if (!isFirebaseConfigured() || !db) {
        console.log('[Firestore] Not configured, returning null');
        return null;
    }

    try {
        const docRef = getUserDocRef(userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as UserData;
        } else {
            // Initialize new user
            await setDoc(docRef, defaultUserData);
            return defaultUserData;
        }
    } catch (error) {
        console.error('[Firestore] Error fetching user data:', error);
        return null;
    }
};

// Save complete user data
export const saveUserData = async (userId: string, data: Partial<UserData>): Promise<boolean> => {
    if (!isFirebaseConfigured() || !db) {
        console.log('[Firestore] Not configured, skipping save');
        return false;
    }

    try {
        const docRef = getUserDocRef(userId);
        await updateDoc(docRef, {
            ...data,
            lastUpdated: new Date().toISOString(),
        });
        return true;
    } catch (error) {
        console.error('[Firestore] Error saving user data:', error);
        return false;
    }
};

// Update specific field
export const updateUserField = async <K extends keyof UserData>(
    userId: string,
    field: K,
    value: UserData[K]
): Promise<boolean> => {
    if (!isFirebaseConfigured() || !db) {
        return false;
    }

    try {
        const docRef = getUserDocRef(userId);
        await updateDoc(docRef, {
            [field]: value,
            lastUpdated: new Date().toISOString(),
        });
        return true;
    } catch (error) {
        console.error(`[Firestore] Error updating ${field}:`, error);
        return false;
    }
};

// Real-time listener
export const subscribeToUserData = (
    userId: string,
    callback: (data: UserData | null) => void
): Unsubscribe | null => {
    if (!isFirebaseConfigured() || !db) {
        console.log('[Firestore] Not configured, no subscription created');
        return null;
    }

    try {
        const docRef = getUserDocRef(userId);
        return onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                callback(docSnap.data() as UserData);
            } else {
                callback(null);
            }
        }, (error) => {
            console.error('[Firestore] Subscription error:', error);
            callback(null);
        });
    } catch (error) {
        console.error('[Firestore] Error setting up subscription:', error);
        return null;
    }
};

// Check if Firestore is available
export const isFirestoreAvailable = (): boolean => {
    return isFirebaseConfigured() && db !== null;
};

// ============ OAuth Token Storage ============

export interface StoredOAuthTokens {
    access_token: string;
    refresh_token?: string;
    expires_at: number;
    scope: string;
}

export interface StoredAccountInfo {
    email: string;
    name: string;
    picture?: string;
}

// Save OAuth tokens to Firestore
export const saveOAuthTokens = async (
    userId: string,
    tokens: StoredOAuthTokens,
    accountInfo: StoredAccountInfo
): Promise<boolean> => {
    if (!isFirebaseConfigured() || !db) {
        console.log('[Firestore] Not configured, tokens not saved to cloud');
        return false;
    }

    try {
        const docRef = doc(db, 'users', userId, 'integrations', 'google');
        await setDoc(docRef, {
            tokens,
            accountInfo,
            updatedAt: new Date().toISOString(),
        });
        console.log('[Firestore] OAuth tokens saved to cloud');
        return true;
    } catch (error) {
        console.error('[Firestore] Error saving OAuth tokens:', error);
        return false;
    }
};

// Get OAuth tokens from Firestore
export const getOAuthTokens = async (
    userId: string
): Promise<{ tokens: StoredOAuthTokens; accountInfo: StoredAccountInfo } | null> => {
    if (!isFirebaseConfigured() || !db) {
        return null;
    }

    try {
        const docRef = doc(db, 'users', userId, 'integrations', 'google');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                tokens: data.tokens as StoredOAuthTokens,
                accountInfo: data.accountInfo as StoredAccountInfo,
            };
        }
        return null;
    } catch (error) {
        console.error('[Firestore] Error getting OAuth tokens:', error);
        return null;
    }
};

// Delete OAuth tokens from Firestore
export const deleteOAuthTokens = async (userId: string): Promise<boolean> => {
    if (!isFirebaseConfigured() || !db) {
        return false;
    }

    try {
        const { deleteDoc } = await import('firebase/firestore');
        const docRef = doc(db, 'users', userId, 'integrations', 'google');
        await deleteDoc(docRef);
        console.log('[Firestore] OAuth tokens deleted from cloud');
        return true;
    } catch (error) {
        console.error('[Firestore] Error deleting OAuth tokens:', error);
        return false;
    }
};
